import type { IRynReplayEnvelope } from '@ups/xplat/web/core';
import type {
  IRynReplayClientContext,
  IRynReplayViewportMarker,
} from './ryn-replay-stream';

/**
 * Envelope payload readers split out of `ryn-replay-stream.ts` (#31329) to keep
 * that file under the size gate. Same contract: shape-check every field, return
 * undefined rather than throw.
 */

export const readClientContext = (
  envelope: IRynReplayEnvelope
): IRynReplayClientContext | undefined => {
  const payload = envelope.payload as Record<string, unknown> | undefined;
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const num = (key: string): number | undefined => {
    const v = payload[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  };
  const str = (key: string): string | undefined => {
    const v = payload[key];
    if (typeof v !== 'string') {
      return undefined;
    }
    const trimmed = v.trim();
    return trimmed || undefined;
  };
  const context: IRynReplayClientContext = {
    platform: str('platform') ?? envelope.platform,
    userAgent:
      str('userAgent') ??
      (typeof envelope.userAgent === 'string'
        ? envelope.userAgent.trim() || undefined
        : undefined),
    language: str('language'),
    deviceModel: str('deviceModel'),
    sdkVersion: str('sdkVersion'),
    timeZone: str('timeZone'),
    screenWidth: num('screenWidth'),
    screenHeight: num('screenHeight'),
    viewportWidth: num('viewportWidth'),
    viewportHeight: num('viewportHeight'),
    devicePixelRatio: num('devicePixelRatio'),
    appVersion: str('appVersion'),
    buildNumber: str('buildNumber'),
    environment:
      str('environment') ??
      (typeof envelope.environment === 'string'
        ? envelope.environment.trim() || undefined
        : undefined),
  };
  return Object.values(context).some((v) => v !== undefined)
    ? context
    : undefined;
};

const positiveNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : undefined;

export const readViewportMarker = (
  envelope: IRynReplayEnvelope
): IRynReplayViewportMarker | undefined => {
  const payload = envelope.payload as Record<string, unknown> | undefined;
  if (!payload) {
    return undefined;
  }
  const innerWidth = positiveNumber(payload['innerWidth']);
  const innerHeight = positiveNumber(payload['innerHeight']);
  const devicePixelRatio = positiveNumber(payload['devicePixelRatio']);
  if (
    innerWidth === undefined ||
    innerHeight === undefined ||
    devicePixelRatio === undefined
  ) {
    return undefined;
  }
  const visualViewportScale = positiveNumber(payload['visualViewportScale']);
  return {
    ts: envelope.ts,
    innerWidth,
    innerHeight,
    devicePixelRatio,
    ...(visualViewportScale !== undefined ? { visualViewportScale } : {}),
  };
};
